import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useAuth } from "../context/AuthContext";
import {
  fetchSupportTicketsAdmin,
  updateSupportTicketStatus,
} from "../services/api";
import { Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui";

const STATUS_OPTIONS = ["open", "in_progress", "resolved", "closed"];

const AdminSupport = () => {
  const { user, initializing } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState("");

  const isAdmin = useMemo(() => {
    const roles = user?.app_metadata?.roles;
    return Array.isArray(roles) && roles.includes("admin");
  }, [user?.app_metadata]);

  useEffect(() => {
    if (!initializing && !isAdmin) {
      toast.error("Admin access required");
      navigate("/dashboard", { replace: true });
    }
  }, [initializing, isAdmin, navigate]);

  const loadTickets = async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const data = await fetchSupportTicketsAdmin(statusFilter === "all" ? undefined : statusFilter);
      setTickets(data || []);
    } catch (error) {
      console.error("[admin] Failed to load tickets", error);
      toast.error("Unable to load tickets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, isAdmin]);

  const handleStatusChange = async (ticketId, status) => {
    setUpdatingId(ticketId);
    try {
      await updateSupportTicketStatus(ticketId, status);
      toast.success("Ticket updated");
      await loadTickets();
    } catch (error) {
      console.error("[admin] Failed to update ticket", error);
      toast.error("Unable to update ticket");
    } finally {
      setUpdatingId("");
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Support tickets</h1>
          <p className="text-sm text-muted-foreground">
            Admin-only view to triage, filter, and close tickets.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS_OPTIONS.map((status) => (
                <SelectItem key={status} value={status}>
                  {status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={loadTickets} disabled={loading}>
            {loading ? "Loading…" : "Refresh"}
          </Button>
        </div>
      </header>

      <div className="space-y-4">
        {tickets.length === 0 && (
          <div className="rounded-2xl border border-border/60 bg-muted/30 p-6 text-sm text-muted-foreground">
            No tickets found for this filter.
          </div>
        )}
        {tickets.map((ticket) => (
          <div
            key={ticket.id}
            className="rounded-2xl border border-border/60 bg-card p-4 shadow-sm"
          >
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-foreground">
                  {ticket.subject}{" "}
                  <span className="text-xs text-muted-foreground">
                    • {ticket.category}
                  </span>
                </p>
                <p className="text-xs text-muted-foreground">
                  {ticket.email ?? "No email"}{" "}
                  {ticket.name ? `(${ticket.name})` : ""}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Select
                  value={ticket.status}
                  onValueChange={(value) => handleStatusChange(ticket.id, value)}
                  disabled={updatingId === ticket.id}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="mt-3 text-sm text-muted-foreground whitespace-pre-wrap">
              {ticket.message}
            </p>
            {ticket.attachment_url && (
              <a
                href={ticket.attachment_url}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex text-sm font-semibold text-primary hover:underline"
              >
                View attachment
              </a>
            )}
            <p className="mt-2 text-xs text-muted-foreground">
              {ticket.created_at}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminSupport;

